import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { InvoiceHeader, DocSubmitTypeEnum, ManifestGroup, ManifestGroupInvoice } from 'app/model/entities/entity-model';
import { InvoiceStatusCode } from 'app/model/enums/invoice-status-code';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { AgStyleFns } from 'app/shared/ag-style-fns';
import { DateFns } from 'app/shared/date-fns';
import { NavFns } from 'app/shared/nav-fns';
import { MenuAction } from 'app/shared/menu-renderer.component';
import { DomainBaseComponent, wrapBusy } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { UtilFns } from 'app/shared/util-fns';
import { InvoiceShipmentService } from './invoice-shipment.service';
import { InvoiceDialogComponent } from '@/shared/invoice-dialog.component';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { BatchSummaryDialogComponent, BatchSummaryFailureRow } from './batch-summary-dialog.component';
import { QbHealthService } from '@/shared/qb-health.service';
import { InvoiceProcessingService, InvoicePostAction, StepStatus } from './invoice-processing.service';

// Per-row outcome from a batch run. Aggregated into the end-of-batch summary.
interface BatchRowResult {
  action: InvoicePostAction;
  success: boolean;
  failedStep: string | null;
  error: string | null;
}

@Component({
  selector: 'app-end-of-day',
  templateUrl: './end-of-day.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class EndOfDayComponent extends DomainBaseComponent {
  gridOptions!: GridOptionsExt;
  invoiceActions: InvoicePostAction[] = [];
  private isProcessing = false;
  // Drives the toolbar "Select All" checkbox visual state. Updated only by
  // toggleSelectAll (user click) and clearAllBatchChecks (post-batch cleanup).
  isSelectAllChecked = false;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private invoiceShipmentService: InvoiceShipmentService,
    public qbHealth: QbHealthService,
    private invoiceProcessing: InvoiceProcessingService,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => this.updateFromContext());
  }

  async updateFromContext() {
    this.gridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onGridReady,
        getRowId: (params) => params.data?.invoiceHeader?.id?.toString(),
      },
      { detailProperty: 'subordinateActions' },
    );
    this.updatePendingMasterDetail(this.gridOptions);

    await this.loadData();
    this.isPageReady = true;
    await this.qbHealth.refresh();
  }

  /** Preflight guard for any per-row action that needs QBO. Refreshes health, shows a toast
   *  and refreshes the grid (so the disabled state takes effect) if unhealthy. */
  private async assertQbHealthy(): Promise<boolean> {
    if (await this.qbHealth.refresh()) return true;
    this.toastr.error(
      'QuickBooks is not connected. Reconnect at QuickBooks Account Setup before continuing.',
      'QuickBooks'
    );
    this.refreshGrids();
    return false;
  }

  onGridReady = async (event: GridReadyEvent) => {
    const colDefs: ColDef[] = [
      // Per-row batch-selection checkbox. Mirrors the established pattern from
      // purchase-orders.component.ts (Force Cancel Open Qty) — state on the row,
      // no parallel selection model, validated at click time.
      {
        ...AgFns.createCheckboxProps('', '_isBatchChecked', 'batchCheckbox'),
        valueGetter: (p: any) => !!p.data?._isBatchChecked,
        valueSetter: (p: any) => {
          if (!p.data) return false;
          p.data._isBatchChecked = !!p.newValue;
          return true;
        },
        editable: true,
        sortable: false,
        filter: false,
        ...AgFns.fixWidth(50),
      },
      {
        colId: 'actions',
        ...AgFns.createMenuProps('', this.getMenuActions(), { buttonLabel: 'Actions' }),
        ...AgFns.fixWidth(130),
      },
      {
        ...AgFns.createButtonProps('', this.processInvoice.bind(this), {
          calcLabel: (action: InvoicePostAction) => {
            const count = 1 + action.subordinateActions.length;
            return count > 1 ? `Process ${count}` : 'Process';
          },
          style: 'button',
          canDisplay: (action: InvoicePostAction) =>
            this.hasGlAccountRef(action) &&
            this.qbHealth.isQbHealthy &&
            !this.isProcessing &&
            !this.needsPCardCharge(action) &&
            (action.overallStatus === 'pending' || action.overallStatus === 'error'),
        }),
        ...AgFns.fixWidth(110),
      },
      {
        headerName: 'Invoice #',
        field: 'invoiceHeader.id',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.fixWidth(140),
        cellClass: AgStyleFns.getLinkCellClass(),
        onCellClicked: (event: any) => {
          const joHeaderId = event.data?.invoiceHeader?.joHeaderId;
          if (joHeaderId)
            NavFns.navigateWithClickEvent(event, this.router, '/in-process/manage/job-orders', NavFns.buildNavIdParams(joHeaderId));
        },
        tooltipValueGetter: (params: any) => params.data?.errorMessage,
      },
      { headerName: 'Account', field: 'invoiceHeader.joHeader.account.accountName', ...AgFns.flexWidth(160) },
      {
        headerName: 'GL Account Ref',
        field: 'invoiceHeader.joHeader.account.gLAccountRef',
        valueFormatter: (params: any) => params.value || '— missing —',
        cellStyle: (params: any) => !params.value ? { color: 'var(--p-red-500, #ef4444)', fontWeight: 600 } : null,
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Account User',
        valueGetter: (params: any) => params.data?.invoiceHeader?.joHeader?.getFormalEmployeeName(),
        ...AgFns.flexWidth(140),
      },
      { headerName: 'Ship To', field: 'invoiceHeader.joHeader.shippingName', ...AgFns.flexWidth(160) },
      {
        headerName: 'Amount',
        field: 'amount',
        ...AgFns.fixWidth(100),
        type: 'numericColumn',
        valueFormatter: (params: any) => (params.value != null ? UtilFns.fmtCurrency(params.value) : ''),
      },
      {
        headerName: 'Deliver',
        ...AgFns.fixWidth(160),
        valueGetter: (params: any) => params.data,
        cellRenderer: (params: any) => this.renderDeliverCell(params.data),
        tooltipValueGetter: (params: any) => (params.data?.deliverStatus === 'error' ? params.data?.errorMessage : null),
      },
      {
        headerName: 'Save PDF',
        ...AgFns.fixWidth(100),
        valueGetter: (params: any) => params.data?.savePdfStatus,
        cellRenderer: (params: any) => this.renderPendingStepCell(params.data?.savePdfStatus, 'Save PDF'),
        tooltipValueGetter: (params: any) => (params.data?.savePdfStatus === 'error' ? params.data?.errorMessage : null),
      },
      {
        headerName: 'P-Card',
        ...AgFns.fixWidth(100),
        valueGetter: (params: any) => params.data?.pCardStatus,
        cellRenderer: (params: any) => this.renderPendingStepCell(params.data?.pCardStatus, 'Charge'),
        tooltipValueGetter: (params: any) => (params.data?.pCardStatus === 'error' ? params.data?.errorMessage : null),
      },
      {
        headerName: 'Post QB',
        ...AgFns.fixWidth(100),
        valueGetter: (params: any) => params.data?.postQbStatus,
        cellRenderer: (params: any) => this.renderPendingStepCell(params.data?.postQbStatus, 'Post'),
        tooltipValueGetter: (params: any) => (params.data?.postQbStatus === 'error' ? params.data?.errorMessage : null),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'invoiceHeader.joHeader.account.accountName', sort: 'asc' }]);
  };

  // -- Master-Detail Setup --

  private updatePendingMasterDetail(parentGridOptions: GridOptionsExt) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      {
        headerName: 'Invoice #',
        field: 'invoiceHeader.id',
        ...AgFns.fixWidth(120),
        cellClass: AgStyleFns.getLinkCellClass(),
        onCellClicked: (event: any) => {
          const joHeaderId = event.data?.invoiceHeader?.joHeaderId;
          if (joHeaderId)
            NavFns.navigateWithClickEvent(event, this.router, '/in-process/manage/job-orders', NavFns.buildNavIdParams(joHeaderId));
        },
      },
      {
        headerName: 'Account User',
        valueGetter: (params: any) => params.data?.invoiceHeader?.joHeader?.getFormalEmployeeName(),
        ...AgFns.flexWidth(140),
      },
      { headerName: 'Ship To', field: 'invoiceHeader.joHeader.shippingName', ...AgFns.flexWidth(160) },
      {
        headerName: 'Amount',
        field: 'amount',
        ...AgFns.fixWidth(100),
        type: 'numericColumn',
        valueFormatter: (params: any) => (params.value != null ? UtilFns.fmtCurrency(params.value) : ''),
      },
      {
        headerName: 'Deliver',
        ...AgFns.fixWidth(140),
        valueGetter: (params: any) => params.data,
        cellRenderer: (params: any) => this.renderDeliverCell(params.data),
      },
      {
        headerName: 'Save PDF',
        ...AgFns.fixWidth(80),
        valueGetter: (params: any) => params.data?.savePdfStatus,
        cellRenderer: (params: any) => this.renderPendingStepCell(params.data?.savePdfStatus, 'Save PDF'),
      },
      {
        headerName: 'P-Card',
        ...AgFns.fixWidth(80),
        valueGetter: (params: any) => params.data?.pCardStatus,
        cellRenderer: (params: any) => this.renderPendingStepCell(params.data?.pCardStatus, 'Charge'),
      },
      {
        headerName: 'Post QB',
        ...AgFns.fixWidth(80),
        valueGetter: (params: any) => params.data?.postQbStatus,
        cellRenderer: (params: any) => this.renderPendingStepCell(params.data?.postQbStatus, 'Post'),
      },
    ];
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions,
      getDetailRowData: (params: any) => params.successCallback(params.data.subordinateActions),
    };
  }

  // -- Data Loading --

  private async loadData() {
    const invoiceHeaders = await this.dbQueryService.getInvoicesReadyForEod();

    // Build all actions first
    const allActions = invoiceHeaders.map((invh) => this.buildAction(invh));

    // Group by shipmentId — primary invoice gets the row, subordinates become detail rows
    const byShipment = _.groupBy(allActions, (a) => a.invoiceHeader.shipmentId ?? a.invoiceHeader.id);

    this.invoiceActions = [];
    for (const group of Object.values(byShipment)) {
      const primary = group.find((a) => a.invoiceHeader.isPrimaryInvoice) ?? group[0];
      primary.subordinateActions = group.filter((a) => a !== primary);
      this.invoiceActions.push(primary);
    }

    // Load today's posted invoices for the processed grid
  }

  private buildAction(invh: InvoiceHeader): InvoicePostAction {
    const account = invh.joHeader.account;
    const isManifested = invh.joHeader.manifestId != null;
    const isAccountOwned = invh.joHeader.isAccountOwnedOrder;
    const deliveryMethod = account.invoiceDocSubmitTypeId;

    const needsDelivery =
      !isManifested &&
      !isAccountOwned &&
      (deliveryMethod === DocSubmitTypeEnum.Print ||
        deliveryMethod === DocSubmitTypeEnum.Email ||
        deliveryMethod === DocSubmitTypeEnum.PrintAndEmail);

    const hasPCard = !!invh.joHeader.pCardCustomerProfileId && !!invh.joHeader.pCardPaymentProfileId;

    return {
      invoiceHeader: invh,
      deliveryMethod,
      shouldSaveToPdf: account.saveOutputToPdf,
      isManifested,
      manifestName: isManifested ? (invh.joHeader.manifest?.manifestName ?? 'Unknown') : undefined,
      isAccountOwned,
      hasPCard,
      amount: invh.roundedTotalAmt(),

      deliverStatus: needsDelivery ? 'pending' : 'n/a',
      savePdfStatus: account.saveOutputToPdf ? 'pending' : 'n/a',
      pCardStatus: hasPCard ? 'pending' : 'n/a',
      postQbStatus: 'pending',

      overallStatus: 'pending',
      errorMessage: null,
      processedTs: null,
      subordinateActions: [],
    };
  }

  // -- Summary --

  get summaryText(): string {
    const primaryCount = this.invoiceActions.length;
    const totalCount = primaryCount + _.sumBy(this.invoiceActions, (a) => a.subordinateActions.length);
    const total = _.sumBy(this.invoiceActions, (a) => a.amount + _.sumBy(a.subordinateActions, (s) => s.amount));
    return `${totalCount} invoice${totalCount !== 1 ? 's' : ''} (${primaryCount} shipment${primaryCount !== 1 ? 's' : ''}) pending — Total: ${UtilFns.fmtCurrency(total)}`;
  }

  // -- Menu Actions --

  // Account has a QBO customer linkage; posting-related actions require this.
  // Only Void Shipping Label and View Invoice are exempt.
  private hasGlAccountRef(action: InvoicePostAction): boolean {
    return !!action?.invoiceHeader?.joHeader?.account?.gLAccountRef;
  }

  getMenuActions(): MenuAction[] {
    const invoiceLabel = (a: InvoicePostAction, verb: string) => {
      const count = 1 + a.subordinateActions.length;
      return count > 1 ? `${verb} ${count} Invoices` : `${verb} Invoice`;
    };

    return [
      {
        label: 'Deliver Invoice',
        calcLabel: (a: InvoicePostAction) => invoiceLabel(a, 'Deliver'),
        icon: 'pi pi-send',
        action: (action: InvoicePostAction) => this.deliverInvoice(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) && this.qbHealth.isQbHealthy && !this.isProcessing && !this.needsPCardCharge(action) && (action.deliverStatus === 'pending' || action.deliverStatus === 'error'),
      },
      {
        label: 'Save PDF',
        calcLabel: (a: InvoicePostAction) => {
          const count = 1 + a.subordinateActions.length;
          return count > 1 ? `Save ${count} PDFs` : 'Save PDF';
        },
        icon: 'pi pi-file-pdf',
        action: (action: InvoicePostAction) => this.savePdf(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) && this.qbHealth.isQbHealthy && !this.isProcessing && !this.needsPCardCharge(action) && (action.savePdfStatus === 'pending' || action.savePdfStatus === 'error'),
      },
      {
        label: 'Charge P-Card',
        calcLabel: (a: InvoicePostAction) => {
          const count = 1 + a.subordinateActions.length;
          return count > 1 ? `Charge ${count} P-Cards` : 'Charge P-Card';
        },
        icon: 'pi pi-credit-card',
        action: (action: InvoicePostAction) => this.chargePCard(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) && this.qbHealth.isQbHealthy && !this.isProcessing && (action.pCardStatus === 'pending' || action.pCardStatus === 'error'),
      },
      {
        label: 'Post to QuickBooks',
        icon: 'pi pi-upload',
        action: (action: InvoicePostAction) => this.postToQb(action),
        canDisplay: (action: InvoicePostAction) =>
          this.hasGlAccountRef(action) && this.qbHealth.isQbHealthy && !this.isProcessing && !this.needsPCardCharge(action) && (action.postQbStatus === 'pending' || action.postQbStatus === 'error'),
      },
      { label: '', action: () => {}, separator: true },
      {
        label: 'Void Shipping Label',
        icon: 'pi pi-times-circle',
        action: (action: InvoicePostAction) => this.voidLabel(action),
        canDisplay: (action: InvoicePostAction) =>
          this.qbHealth.isQbHealthy &&
          !this.isProcessing &&
          action.pCardStatus !== 'success' &&
          action.invoiceHeader.isPrimaryInvoice &&
          action.invoiceHeader.invoiceStatusId === InvoiceStatusCode.VoucherLabeled &&
          !action.invoiceHeader.isPickup,
      },
      {
        // Pickup shipments reach VoucherLabeled with no carrier label to void — voiding one
        // would fail the ShipEngine call and strand the shipment's invoices in VOIDERR.
        label: 'Undo Pickup Release',
        icon: 'pi pi-replay',
        action: (action: InvoicePostAction) => this.undoPickupRelease(action),
        canDisplay: (action: InvoicePostAction) =>
          !this.isProcessing &&
          action.pCardStatus !== 'success' &&
          action.invoiceHeader.isPrimaryInvoice &&
          action.invoiceHeader.invoiceStatusId === InvoiceStatusCode.VoucherLabeled &&
          action.invoiceHeader.isPickup,
      },
      {
        label: 'View Invoice',
        icon: 'pi pi-eye',
        action: (action: InvoicePostAction) => this.viewInvoice(action),
      },
    ];
  }

  // -- Batch Selection (toolbar checkbox + Process Selected) --

  // Toggles the _isBatchChecked flag on every primary row currently in the
  // pending grid. Subordinate actions are NOT individually checked — the
  // checkbox is per shipment (primary row), matching the per-row Process
  // button's behavior (one click processes primary + subordinates as a unit).
  // Rows whose account lacks a GLAccountRef are skipped — they can't post to QBO.
  toggleSelectAll(checked: boolean) {
    this.isSelectAllChecked = checked;
    this.gridOptions.apiX?.forEachNode((node) => {
      const action = node.data as InvoicePostAction;
      if (!action) return;
      if (checked && !this.hasGlAccountRef(action)) return;
      (action as any)._isBatchChecked = checked;
    });
    this.gridOptions.apiX?.refreshCells({ columns: ['batchCheckbox'], force: true });
  }

  // Clears every row's checkbox state and resets the header toggle. Called
  // after a batch run finishes so the next batch starts clean.
  private clearAllBatchChecks() {
    this.isSelectAllChecked = false;
    this.gridOptions.apiX?.forEachNode((node) => {
      const action = node.data as InvoicePostAction;
      if (action) (action as any)._isBatchChecked = false;
    });
    this.gridOptions.apiX?.refreshCells({ columns: ['batchCheckbox'], force: true });
  }

  // Walks the pending grid for rows whose checkbox is currently set. Returns
  // only rows that are in a state where processing would do useful work — pending
  // or error, and whose account has a GLAccountRef (posting requires it).
  // Rows already in a terminal in-progress / success state, or missing a
  // GLAccountRef, are silently skipped.
  private collectCheckedActionsForBatch(): InvoicePostAction[] {
    const checked: InvoicePostAction[] = [];
    this.gridOptions.apiX?.forEachNode((node) => {
      const action = node.data as InvoicePostAction;
      if (!action || !(action as any)._isBatchChecked) return;
      if (!this.hasGlAccountRef(action)) return;
      if (action.overallStatus === 'pending' || action.overallStatus === 'error') {
        checked.push(action);
      }
    });
    return checked;
  }

  // Standard confirmation: shows count + p-card total + irreversibility warning.
  // Counts ALL invoices being processed (primary + every subordinate), since
  // subordinates ride with their primary. Counts P-Card cards distinctly (multiple
  // invoices may share a single card) and totals the dollar amount that will hit
  // gateways the moment the operator clicks Yes.
  private buildConfirmationMessage(actions: InvoicePostAction[]): string {
    const primaryCount = actions.length;
    const totalInvoiceCount = primaryCount + _.sumBy(actions, (a) => a.subordinateActions.length);

    const allActions = actions.flatMap((a) => [a, ...a.subordinateActions]);
    const pCardActions = allActions.filter((a) => this.needsPCardCharge(a));
    const pCardCount = pCardActions.length;
    const pCardTotal = _.sumBy(pCardActions, (a) => a.amount);

    const lines: string[] = [];
    lines.push(`Process ${totalInvoiceCount} invoice${totalInvoiceCount !== 1 ? 's' : ''} across ${primaryCount} shipment${primaryCount !== 1 ? 's' : ''}?`);
    lines.push('');
    if (pCardCount > 0) {
      lines.push(`${pCardCount} invoice${pCardCount !== 1 ? 's require' : ' requires'} P-Card charges totaling ${UtilFns.fmtCurrency(pCardTotal)} before posting to QuickBooks.`);
      lines.push('');
      lines.push('This will charge customer cards immediately and cannot be undone.');
    } else {
      lines.push('No P-Card charges required for the selected invoices.');
    }
    return lines.join('<br/>');
  }

  async processSelectedInvoices() {
    if (this.isProcessing) return;

    const checked = this.collectCheckedActionsForBatch();
    if (checked.length === 0) {
      await this.dialogService.okDialog('Nothing Selected',
        'Check at least one pending invoice before clicking Process Selected.');
      return;
    }

    const message = this.buildConfirmationMessage(checked);
    const confirmed = await this.dialogService.yesNoDialog('Process Selected Invoices?', message);
    if (!confirmed) return;

    // QB setup check happens once for the whole batch, not per row. If QB isn't
    // configured, nothing downstream can succeed and we bail before any work.
    const qbErr = await this.dbQueryService.checkQuickbooksSetup();
    if (qbErr) {
      await this.dialogService.okDialog('QuickBooks', qbErr);
      return;
    }

    this.isProcessing = true;
    const results: BatchRowResult[] = [];

    try {
      for (const action of checked) {
        const result = await this.processOneRowForBatch(action);
        results.push(result);
        // Refresh once per row so the operator sees live progress without
        // having to wait for the whole batch.
        this.refreshGrids();
      }
    } finally {
      this.isProcessing = false;
      this.clearAllBatchChecks();
      this.refreshGrids();
    }

    // Compact summary dialog: surfaces the headline numbers and a per-failure
    // breakdown. The grid is the live source of truth (failures stay in pending
    // with their step status shown via cell renderers); the dialog gives the
    // operator the closing recap.
    const successCount = results.filter((r) => r.success).length;
    const failures: BatchSummaryFailureRow[] = results
      .filter((r) => !r.success)
      .map((r) => ({
        invoiceId: r.action.invoiceHeader.id,
        accountName: r.action.invoiceHeader.joHeader.account?.accountName ?? '',
        failedStep: r.failedStep ?? 'Unknown',
        error: r.error ?? '',
      }));

    await DialogBaseComponent.open(BatchSummaryDialogComponent, this, {
      successCount,
      failureCount: failures.length,
      failures,
    });
  }

  // Runs one row through the full pipeline, with Step 0 (p-card charge) added
  // when needed. Wraps the whole sequence in try/catch so a single row's failure
  // does not stop the batch. Returns a BatchRowResult that the caller aggregates
  // into the end-of-batch summary.
  //
  // Step-level idempotency is delegated to the per-step Core methods — each one
  // short-circuits on its own "did this already happen?" predicate (see commits
  // a8e33052, 8fe78e9d, f445cd7d, ce270b10). So a row retried partway through
  // the pipeline picks up where it left off rather than re-doing earlier steps.
  private async processOneRowForBatch(action: InvoicePostAction): Promise<BatchRowResult> {
    const allActions = [action, ...action.subordinateActions];
    for (const a of allActions) {
      a.overallStatus = 'in-progress';
      a.errorMessage = null;
    }

    let failedStep: string | null = null;

    try {
      // Step 0: Charge P-Card if needed. Blocks subsequent steps for this row
      // until the gateway returns success. Failure here marks the row errored
      // and short-circuits the rest of the pipeline for this row only.
      if (this.needsPCardCharge(action)) {
        failedStep = 'Charge P-Card';
        await this.chargePCardCore(action);
      }

      // Step 1: Deliver (print + email). Includes the primary + subordinates
      // via includeAll when subordinates exist. Batch path requires QZ Tray
      // and a configured invoicePrinter — see silentPrintViaQz for the
      // pre-flight check and the rationale for not falling back to window.open
      // when QZ is unavailable (per the design, the row fails rather than
      // silently degrading to a preview the operator can't see).
      if (action.deliverStatus === 'pending' || action.deliverStatus === 'error') {
        failedStep = 'Deliver';
        await this.deliverInvoiceCore(action, action.subordinateActions.length > 0, true);
        for (const sub of action.subordinateActions) sub.deliverStatus = action.deliverStatus;
      }

      // Step 2: Save PDF to drive.
      if (action.savePdfStatus === 'pending' || action.savePdfStatus === 'error') {
        failedStep = 'Save PDF';
        await this.savePdfCore(action);
        for (const sub of action.subordinateActions) sub.savePdfStatus = action.savePdfStatus;
      }

      // Step 3: Advance to Printed status. Idempotent on its own.
      failedStep = 'Advance to Printed';
      await this.advanceToPrinted(action, allActions);

      // Step 4: Post to QB.
      if (action.postQbStatus === 'pending' || action.postQbStatus === 'error') {
        failedStep = 'Post QB';
        await this.postToQbCore(action);
      }

      // Step 5: Create ManifestGroup linkage. Best-effort — failure here
      // doesn't fail the whole row since QB has already posted.
      try {
        await this.createManifestGroupForActions(allActions);
      } catch (mgErr: any) {
        this.toastr.error(`Manifest group creation failed for invoice ${action.invoiceHeader.id}: ${mgErr.message || 'Unknown'}. QB post succeeded.`);
      }

      const now = new Date();
      for (const a of allActions) {
        a.overallStatus = 'success';
        a.postQbStatus = 'success';
        a.processedTs = now;
      }
      this.invoiceActions = this.invoiceActions.filter((a) => a !== action);
      return { action, success: true, failedStep: null, error: null };
    } catch (err: any) {
      const errorMessage = err?.message || 'Unknown error';
      for (const a of allActions) {
        if (a.overallStatus === 'in-progress') {
          a.overallStatus = 'error';
          a.errorMessage = errorMessage;
        }
      }
      return { action, success: false, failedStep: failedStep, error: errorMessage };
    }
  }

  // -- Processing: All Steps --

  async processInvoice(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    if (!(await this.assertQbHealthy())) {
      this.isProcessing = false;
      return;
    }

    const allActions = [action, ...action.subordinateActions];
    for (const a of allActions) {
      a.overallStatus = 'in-progress';
      a.errorMessage = null;
    }

    try {
      // Step 1: Deliver primary (prints/emails all invoices on shipment via all=true)
      if (action.deliverStatus === 'pending' || action.deliverStatus === 'error') {
        await this.deliverInvoiceCore(action, action.subordinateActions.length > 0);
        for (const sub of action.subordinateActions) {
          sub.deliverStatus = action.deliverStatus;
        }
      }

      // Step 2: Save PDF for primary (saves all invoices on shipment via all=true)
      if (action.savePdfStatus === 'pending' || action.savePdfStatus === 'error') {
        await this.savePdfCore(action);
        for (const sub of action.subordinateActions) {
          sub.savePdfStatus = action.savePdfStatus;
        }
      }

      // Step 3: Advance primary to Printed and persist delivery info (skip if already done)
      await this.advanceToPrinted(action, allActions);

      // Step 4: Post to QB (server handles all invoices on the shipment)
      if (action.postQbStatus === 'pending' || action.postQbStatus === 'error') {
        await this.postToQbCore(action);
      }

      // Step 5: Create ManifestGroup for manifested invoices (non-blocking)
      try {
        await this.createManifestGroupForActions(allActions);
      } catch (mgErr: any) {
        this.toastr.error(`Manifest group creation failed: ${mgErr.message || 'Unknown error'}. Invoice posting succeeded.`);
      }

      // Mark all as success
      const now = new Date();
      for (const a of allActions) {
        a.overallStatus = 'success';
        a.postQbStatus = 'success';
        a.processedTs = now;
      }
      this.invoiceActions = this.invoiceActions.filter((a) => a !== action);
    } catch (err: any) {
      for (const a of allActions) {
        if (a.overallStatus === 'in-progress') {
          a.overallStatus = 'error';
          a.errorMessage = err.message || 'Unknown error';
        }
        // Any per-step status left at 'in-progress' must be reset, or the row spinner stays
        // stuck even after the row's overall status flips to 'error'.
        if (a.deliverStatus === 'in-progress') a.deliverStatus = 'error';
        if (a.savePdfStatus === 'in-progress') a.savePdfStatus = 'error';
        if (a.pCardStatus === 'in-progress') a.pCardStatus = 'error';
        if (a.postQbStatus === 'in-progress') a.postQbStatus = 'error';
      }
    } finally {
      this.isProcessing = false;
      this.refreshGrids();
    }
  }

  // -- Processing: Individual Steps --

  async deliverInvoice(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    if (!(await this.assertQbHealthy())) {
      this.isProcessing = false;
      return;
    }
    try {
      await this.deliverInvoiceCore(action, action.subordinateActions.length > 0);
      for (const sub of action.subordinateActions) {
        sub.deliverStatus = action.deliverStatus;
      }
    } catch (err: any) {
      action.deliverStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err.message || 'Delivery failed';
    } finally {
      this.isProcessing = false;
      this.refreshGrids();
    }
  }

  async savePdf(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    if (!(await this.assertQbHealthy())) {
      this.isProcessing = false;
      return;
    }
    try {
      await this.savePdfCore(action);
      for (const sub of action.subordinateActions) {
        sub.savePdfStatus = action.savePdfStatus;
      }
    } catch (err: any) {
      action.savePdfStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err.message || 'PDF save failed';
    } finally {
      this.isProcessing = false;
      this.refreshGrids();
    }
  }

  async chargePCard(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    if (!(await this.assertQbHealthy())) {
      this.isProcessing = false;
      return;
    }
    try {
      await this.chargePCardCore(action);
    } catch (err: any) {
      action.pCardStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err.message || 'P-Card charge failed';
    } finally {
      this.isProcessing = false;
      this.refreshGrids();
    }
  }

  async postToQb(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    if (!(await this.assertQbHealthy())) {
      this.isProcessing = false;
      return;
    }

    try {
      const allActions = [action, ...action.subordinateActions];
      await this.advanceToPrinted(action, allActions);
      await this.postToQbCore(action);
      await this.createManifestGroupForActions(allActions);
    } catch (err: any) {
      action.postQbStatus = 'error';
      action.overallStatus = 'error';
      action.errorMessage = err.message || 'QB posting failed';
    } finally {
      this.isProcessing = false;
      this.refreshGrids();
    }
  }

  // -- Core Step Implementations (delegated to InvoiceProcessingService) --

  private async advanceToPrinted(action: InvoicePostAction, allActions: InvoicePostAction[]) {
    if (action.invoiceHeader.invoiceStatusId === InvoiceStatusCode.Printed) return;
    for (const a of allActions) {
      a.invoiceHeader.deliveredBy = this.invoiceProcessing.getDeliveredByString(a);
      a.invoiceHeader.savedToPdf = a.shouldSaveToPdf ? a.invoiceHeader.joHeader.portalName || 'Default' : undefined;
      a.invoiceHeader.manifestName = a.manifestName;
    }
    action.invoiceHeader.invoiceStatusId = InvoiceStatusCode.Printed;
    await this.dbSaveService.saveChanges();
  }

  private deliverInvoiceCore(action: InvoicePostAction, includeAll = false, useSilentPrint = false) {
    return this.invoiceProcessing.deliverInvoiceCore(action, includeAll, useSilentPrint, () => this.refreshGrids());
  }

  private savePdfCore(action: InvoicePostAction) {
    return this.invoiceProcessing.savePdfCore(action, () => this.refreshGrids());
  }

  private chargePCardCore(action: InvoicePostAction) {
    return this.invoiceProcessing.chargePCardCore(action, () => this.refreshGrids());
  }

  private postToQbCore(action: InvoicePostAction) {
    return this.invoiceProcessing.postToQbCore(action, () => this.refreshGrids());
  }

  private async createManifestGroupForActions(allActions: InvoicePostAction[]) {
    const manifestedActions = allActions.filter((a) => a.isManifested);
    if (manifestedActions.length === 0) return;

    const byManifestId = _.groupBy(manifestedActions, (a) => a.invoiceHeader.joHeader.manifestId);

    for (const [manifestId, actions] of Object.entries(byManifestId)) {
      const manifest = actions[0].invoiceHeader.joHeader.manifest;
      if (!manifest) continue;

      // Find an unsubmitted ManifestGroup for this manifest.
      // Once submitted (sentDate is set), it's sealed — create a new one instead.
      const existingGroups = await this.uow
        .createQuery(ManifestGroup, 'ManifestGroups')
        .where({ manifestId: +manifestId, sentDate: null })
        .execute();

      const isExisting = existingGroups.length > 0;
      let mg = isExisting ? existingGroups[0] : null;
      if (!mg) {
        mg = this.uow.createEntity(ManifestGroup, {
          manifestId: +manifestId,
        });
      }

      // Idempotency guard: when we're reusing an existing ManifestGroup, the (mg, invh)
      // junction row may already exist from a prior attempt. Query once for the whole
      // group and skip per-invoice inserts whose pair is already there. For a fresh
      // ManifestGroup (just created above), the junction is guaranteed empty so we skip
      // the query entirely.
      let existingPairs: Set<string> = new Set<string>();
      if (isExisting) {
        const existingJunctions = await this.uow
          .createQuery(ManifestGroupInvoice, 'ManifestGroupInvoices')
          .where({ manifestGroupId: mg.id })
          .execute();
        existingPairs = new Set(existingJunctions.map(j => j.invoiceHeaderId));
      }

      const addedActions: InvoicePostAction[] = [];
      for (const action of actions) {
        if (existingPairs.has(action.invoiceHeader.id)) continue;
        this.uow.createEntity(ManifestGroupInvoice, {
          manifestGroup: mg,
          invoiceHeader: action.invoiceHeader,
        });
        addedActions.push(action);
      }

      // Nothing new to persist for this manifest? Skip the save and toast.
      if (addedActions.length === 0 && isExisting) continue;

      await this.dbSaveService.saveChanges();
      await this.dbSaveService.updateManifestAndRelated(manifest);

      const invoiceIds = addedActions.map(a => a.invoiceHeader.id).join(', ');
      if (isExisting) {
        this.toastr.info(`Invoices ${invoiceIds} applied to existing Manifest Group ${mg.id}`);
      } else {
        this.toastr.info(`A new Manifest Group, #${mg.id} has been created for invoices ${invoiceIds}`);
      }
    }
  }

  // -- Void Label --

  async voidLabel(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    if (!(await this.assertQbHealthy())) {
      this.isProcessing = false;
      return;
    }
    try {
      const ok = await this.invoiceShipmentService.voidShippingLabel(action.invoiceHeader);
      if (ok) {
        this.invoiceActions = this.invoiceActions.filter((a) => a !== action);
        this.refreshGrids();
        this.domainService.toastService.success('Shipping label voided. Invoice returned to In-Process Shipping.');
      }
    } finally {
      this.isProcessing = false;
    }
  }

  // -- Undo Pickup Release --

  // The pickup counterpart to voidLabel. No carrier call, so no QB-health gate and
  // nothing that can land the shipment in VOIDERR — it just returns the invoices to
  // In-Process Shipping so they can be re-released or shipped by carrier instead.
  async undoPickupRelease(action: InvoicePostAction) {
    if (this.isProcessing) return;
    this.isProcessing = true;
    try {
      const ok = await this.dialogService.yesNoDialog(
        'Undo Pickup Release',
        `Return invoice ${action.invoiceHeader.id} to In-Process Shipping?<br><br>It will drop off End of Day.`,
      );
      if (!ok) return;

      const undone = await this.invoiceShipmentService.undoPickupRelease(action.invoiceHeader);
      if (undone) {
        this.invoiceActions = this.invoiceActions.filter((a) => a !== action);
        this.refreshGrids();
        this.domainService.toastService.success('Pickup release undone. Invoice returned to In-Process Shipping.');
      }
    } finally {
      this.isProcessing = false;
    }
  }

  // -- View Invoice --

  async viewInvoice(action: InvoicePostAction) {
    await InvoiceDialogComponent.openDialog(this.domainService.pngDialogService, {
      invoiceId: action.invoiceHeader.id,
    });
  }

  // -- Refresh --

  @wrapBusy
  async refresh() {
    await this.loadData();
    this.refreshGrids();
  }

  @wrapBusy
  private refreshGrids() {
    this.gridOptions.apiX?.setGridOption('rowData', this.invoiceActions);
    this.gridOptions.apiX?.refreshCells({ force: true });
    this.refreshDetailGrids(this.gridOptions);
  }

  private refreshDetailGrids(gridOptions: GridOptionsExt) {
    gridOptions.apiX?.forEachDetailGridInfo((detail) => {
      detail.api?.refreshCells({ force: true });
    });
  }

  // -- Helpers --

  /** True if the invoice has a P-Card that hasn't been successfully charged yet */
  private needsPCardCharge(action: InvoicePostAction): boolean {
    return action.hasPCard && action.pCardStatus !== 'success';
  }

  // -- Cell Renderers --

  /** Pending grid: show what delivery *will* happen */
  private renderDeliverCell(action: InvoicePostAction): string {
    if (!action) return '';
    const status = action.deliverStatus;

    if (status === 'n/a') {
      const label = this.getDeliveryLabel(action);
      return `<span class="text-color-secondary">${label}</span>`;
    }
    if (status === 'in-progress') return `<i class="pi pi-spinner pi-spin mr-1"></i>${this.getDeliveryLabel(action)}`;
    if (status === 'success') return `<i class="pi pi-check-circle text-green-500 mr-1"></i>${this.getDeliveryLabel(action)}`;
    if (status === 'error') return '<i class="pi pi-times-circle text-red-500 mr-1"></i>Failed';
    // pending — prefix with "Will"
    return `Will ${this.getDeliveryLabel(action)}`;
  }

  /** Processed grid: show what delivery *was done* */
  private renderProcessedDeliverCell(action: InvoicePostAction): string {
    if (!action) return '';
    const status = action.deliverStatus;

    if (status === 'n/a') {
      const label = this.getDeliveryLabel(action);
      return `<span class="text-color-secondary">${label}</span>`;
    }
    if (status === 'success') return `<i class="pi pi-check-circle text-green-500 mr-1"></i>${this.getProcessedDeliveryLabel(action)}`;
    if (status === 'error') return '<i class="pi pi-times-circle text-red-500 mr-1"></i>Failed';
    return this.getDeliveryLabel(action);
  }

  /** Pending grid: show what *will* happen */
  private renderPendingStepCell(status: StepStatus, label: string): string {
    if (!status) return '';
    switch (status) {
      case 'n/a':
        return '<span class="text-color-secondary">\u2014</span>';
      case 'pending':
        return label;
      case 'in-progress':
        return `<i class="pi pi-spinner pi-spin mr-1"></i>${label}`;
      case 'success':
        return `<i class="pi pi-check-circle text-green-500 mr-1"></i>${label}`;
      case 'error':
        return '<i class="pi pi-times-circle text-red-500 mr-1"></i>Failed';
      default:
        return '';
    }
  }

  /** Processed grid: show what *was done* and outcome */
  private renderProcessedStepCell(status: StepStatus, _label: string): string {
    if (!status) return '';
    switch (status) {
      case 'n/a':
        return '<span class="text-color-secondary">\u2014</span>';
      case 'success':
        return '<i class="pi pi-check-circle text-green-500 mr-1"></i>Done';
      case 'error':
        return '<i class="pi pi-times-circle text-red-500 mr-1"></i>Failed';
      default:
        return '';
    }
  }

  private getDeliveryLabel(action: InvoicePostAction): string {
    if (!action) return '';
    if (action.isManifested) return `Manifest: ${action.manifestName}`;
    switch (action.deliveryMethod) {
      case DocSubmitTypeEnum.Print:
        return 'Print';
      case DocSubmitTypeEnum.Email:
        return 'Email';
      case DocSubmitTypeEnum.PrintAndEmail:
        return 'Print & Email';
      case DocSubmitTypeEnum.None:
        return 'None';
      default:
        return 'Unknown';
    }
  }

  private getProcessedDeliveryLabel(action: InvoicePostAction): string {
    if (!action) return '';
    if (action.isManifested) return `Manifest: ${action.manifestName}`;
    switch (action.deliveryMethod) {
      case DocSubmitTypeEnum.Print:
        return 'Printed';
      case DocSubmitTypeEnum.Email:
        return 'Emailed';
      case DocSubmitTypeEnum.PrintAndEmail:
        return 'Printed & Emailed';
      case DocSubmitTypeEnum.None:
        return 'None';
      default:
        return 'Unknown';
    }
  }
}
